import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { EllipsisVertical, Plus } from 'lucide-react';
import { useIntl } from 'react-intl';

import { AddNewStoryButton } from '@/components/add-new-story-button';
import { AddNewStoryDialog } from '@/components/add-new-story-dialog';
import { DeleteStoryDialog } from '@/components/delete-story-dialog';
import { apiGetStories } from '@/shared/api';
import { apiPostStories, APIPostStoriesPayload, Story } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Progress } from '@/shared/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

export const Route = createFileRoute('/_auth/')({
  component: Index,
});

function Index() {
  const intl = useIntl();

  return (
    <div className="px-4 py-6 md:px-10">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="mb-2 text-3xl font-bold">
            {intl.formatMessage({ id: 'homePage.greeting' })}
          </h2>

          <p>{intl.formatMessage({ id: 'homePage.slogan' })}</p>
        </div>

        <AddNewStoryDialog>
          <Button>
            <Plus /> {intl.formatMessage({ id: 'button.addNewStory' })}
          </Button>
        </AddNewStoryDialog>
      </div>

      <Tabs defaultValue="familyStories">
        <TabsList className="mb-8 grid w-full grid-cols-2 md:max-w-[400px]">
          <TabsTrigger value="familyStories" className="text-lg">
            {intl.formatMessage({ id: 'homePage.tabs.familyStories' })}
          </TabsTrigger>

          <TabsTrigger value="yourStories" className="text-lg">
            {intl.formatMessage({ id: 'homePage.tabs.yourStories' })}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="familyStories">
          <FamilyTab />
        </TabsContent>

        <TabsContent value="yourStories">
          <PersonalTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const FamilyTab = () => {
  const intl = useIntl();
  const query = useQuery({ queryKey: ['stories'], queryFn: () => apiGetStories() });

  return (
    <div className="flex flex-wrap gap-4">
      {query.data?.data.map((story) => (
        <Card key={story.id} className="min-w-full sm:max-h-[255px] md:min-w-[380px]">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-2xl">
                {story.name ? story.name : intl.formatMessage({ id: 'story.newStory' })}
              </span>

              <DropdownMenu modal={false}>
                <Button asChild size="icon" variant="ghost">
                  <DropdownMenuTrigger>
                    <EllipsisVertical />
                  </DropdownMenuTrigger>
                </Button>

                <DropdownMenuContent>
                  <DeleteStoryDialog storyId={story.id}>
                    {/* https://github.com/shadcn-ui/ui/issues/2497 */}
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      {intl.formatMessage({ id: 'story.delete' })}
                    </DropdownMenuItem>
                  </DeleteStoryDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardTitle>

            {story.template && (
              <CardDescription>{story.template.description}</CardDescription>
            )}
          </CardHeader>

          <CardContent>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs">
                  {story.progress.percent === 100
                    ? intl.formatMessage({ id: 'story.status.finished' })
                    : intl.formatMessage({ id: 'story.status.inProgress' })}
                </span>

                <span className="text-xs">
                  {intl.formatMessage(
                    { id: 'story.questionsProgress' },
                    {
                      totalAnswers: story.progress.totalAnswers,
                      totalQuestions: story.progress.totalQuestions,
                    },
                  )}
                </span>
              </div>

              <Progress value={story.progress.percent} />
            </div>
          </CardContent>

          <CardFooter>
            <div className="flex w-full justify-end">
              <Button asChild>
                <Link to="/stories/$storyId" params={{ storyId: String(story.id) }}>
                  {intl.formatMessage({ id: 'view' })}
                </Link>
              </Button>
            </div>
          </CardFooter>
        </Card>
      ))}

      <AddNewStoryButton />
    </div>
  );
};

const PersonalTab = () => {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['stories', 'isPersonal'],
    queryFn: () => apiGetStories({ isPersonal: true }),
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation<Story, Error, APIPostStoriesPayload>({
    mutationFn: (payload) => {
      return apiPostStories(payload).then(({ data }) => data);
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: ['stories'],
      });
      navigate({ to: '/stories/$storyId', params: { storyId: id } });
    },
  });

  return (
    <div className="flex flex-wrap gap-4">
      {query.data?.data.map((story) => (
        <Card key={story.id} className="min-w-full sm:max-h-[255px] md:min-w-[380px]">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-2xl">
                {story.name ? story.name : intl.formatMessage({ id: 'story.newStory' })}
              </span>

              <DropdownMenu modal={false}>
                <Button asChild size="icon" variant="ghost">
                  <DropdownMenuTrigger>
                    <EllipsisVertical />
                  </DropdownMenuTrigger>
                </Button>

                <DropdownMenuContent>
                  <DeleteStoryDialog storyId={story.id}>
                    {/* https://github.com/shadcn-ui/ui/issues/2497 */}
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      {intl.formatMessage({ id: 'story.delete' })}
                    </DropdownMenuItem>
                  </DeleteStoryDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardTitle>

            {story.template && (
              <CardDescription>{story.template.description}</CardDescription>
            )}
          </CardHeader>

          <CardContent></CardContent>

          <CardFooter>
            <div className="flex w-full justify-end">
              <Button asChild>
                <Link to="/stories/$storyId" params={{ storyId: String(story.id) }}>
                  {intl.formatMessage({ id: 'view' })}
                </Link>
              </Button>
            </div>
          </CardFooter>
        </Card>
      ))}
      <Card
        className="flex h-full min-w-full cursor-pointer justify-center py-8 sm:max-h-[255px] md:min-w-[380px]"
        onClick={() => {
          mutation.mutate({
            isPersonal: true,
          });
        }}
      >
        <CardContent className="flex h-full max-w-[340px] flex-col items-center justify-center px-8 pb-0 text-center">
          <Plus className="mb-4" size={40} />

          <p className="mb-2 text-2xl font-semibold">
            {intl.formatMessage({ id: 'button.addNewStory' })}
          </p>

          <p>{intl.formatMessage({ id: 'button.addNewPersonalStory.description' })}</p>
        </CardContent>
      </Card>
    </div>
  );
};
